import { useState, useMemo } from "react";
import { X, ChevronUp, ChevronDown, Plus, Search, Globe, Lock, Users, Share2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface Pond {
  id: string;
  name: string;
  type: string;
  isPublic: boolean;
  isOwner: boolean;
  parentId: string | null;
  inviteCode?: string | null;
}

interface PondMapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ponds: Pond[];
  currentPondId: string | null;
  onSelectPond: (pondId: string) => void;
  onCreatePond: () => void;
  onExplorePublic: () => void;
  onJoinPond?: (inviteCode: string) => Promise<boolean>;
}

interface PondNode extends Pond {
  children: PondNode[];
  depth: number;
}

export const PondMapModal = ({
  open,
  onOpenChange,
  ponds,
  currentPondId,
  onSelectPond,
  onCreatePond,
  onExplorePublic,
  onJoinPond,
}: PondMapModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedPonds, setExpandedPonds] = useState<Set<string>>(new Set());
  const [inviteCode, setInviteCode] = useState("");
  const [joiningPond, setJoiningPond] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Build tree structure
  const pondTree = useMemo(() => {
    const buildTree = (parentId: string | null, depth: number): PondNode[] => {
      return ponds
        .filter((p) => p.parentId === parentId)
        .map((pond) => ({
          ...pond,
          depth,
          children: buildTree(pond.id, depth + 1),
        }));
    };
    return buildTree(null, 0);
  }, [ponds]);

  // Filter ponds by search
  const filteredPonds = useMemo(() => {
    if (!searchTerm) return pondTree;
    
    const filterNodes = (nodes: PondNode[]): PondNode[] => {
      return nodes
        .map((node) => ({
          ...node,
          children: filterNodes(node.children),
        }))
        .filter(
          (node) =>
            node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            node.children.length > 0
        );
    };
    return filterNodes(pondTree);
  }, [pondTree, searchTerm]);

  const toggleExpand = (pondId: string) => {
    setExpandedPonds((prev) => {
      const next = new Set(prev);
      if (next.has(pondId)) {
        next.delete(pondId);
      } else {
        next.add(pondId);
      }
      return next;
    });
  };

  const handleSelectPond = (pondId: string) => {
    onSelectPond(pondId);
    onOpenChange(false);
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success("Código copiado!");
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      toast.error("Erro ao copiar código");
    }
  };

  const handleShareWhatsApp = (pondName: string, code: string) => {
    const message = encodeURIComponent(
      `🐸 Entre na minha lagoa "${pondName}" no Habit Frog Tracker!\n\nUse o código: ${code}`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  const handleShareNative = async (pondName: string, code: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Lagoa "${pondName}"`,
          text: `🐸 Entre na minha lagoa "${pondName}" no Habit Frog Tracker!\n\nUse o código: ${code}`,
        });
      } catch (err) {
        // User cancelled or share failed, fallback to copy
        handleCopyCode(code);
      }
    } else {
      handleCopyCode(code);
    }
  };

  const handleJoinPond = async () => {
    if (!inviteCode.trim() || !onJoinPond) return;
    
    setJoiningPond(true);
    try {
      const success = await onJoinPond(inviteCode.trim().toUpperCase());
      if (success) {
        setInviteCode("");
      }
    } finally {
      setJoiningPond(false);
    }
  };

  const renderPondNode = (node: PondNode): React.ReactNode => {
    const isExpanded = expandedPonds.has(node.id);
    const hasChildren = node.children.length > 0;
    const isSelected = node.id === currentPondId;
    const hasInviteCode = node.inviteCode && node.type === "shared";

    return (
      <div key={node.id} className="relative">
        {/* Connection line */}
        {node.depth > 0 && (
          <div
            className="absolute left-0 top-0 h-full w-px bg-border/50"
            style={{ left: `${(node.depth - 1) * 24 + 12}px` }}
          />
        )}

        <div
          className={cn(
            "group flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-all",
            "hover:bg-primary/10",
            isSelected && "bg-primary/20 ring-2 ring-primary"
          )}
          style={{ paddingLeft: `${node.depth * 24 + 12}px` }}
          onClick={() => handleSelectPond(node.id)}
        >
          {/* Expand/collapse button */}
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node.id);
              }}
              className="p-1 hover:bg-background/50 rounded-md"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4 rotate-90" />
              )}
            </button>
          )}

          {/* Pond visual */}
          <div className={cn(
            "relative w-12 h-12 rounded-full flex-shrink-0",
            "bg-gradient-to-br from-pond via-pond-light to-pond-surface",
            "shadow-md group-hover:shadow-lg transition-shadow",
            "flex items-center justify-center"
          )}>
            <span className="text-xl">🐸</span>
            {/* Ripple effect */}
            <div className="absolute inset-0 rounded-full border-2 border-primary-foreground/20 animate-ping opacity-20" />
          </div>

          {/* Pond info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-display font-bold truncate">{node.name}</span>
              {node.type === "private" ? (
                <Lock className="h-3 w-3 text-muted-foreground" />
              ) : node.isPublic ? (
                <Globe className="h-3 w-3 text-muted-foreground" />
              ) : (
                <Users className="h-3 w-3 text-muted-foreground" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {node.isOwner ? "Sua lagoa" : "Membro"}
              {hasChildren && ` • ${node.children.length} sub-lagoa(s)`}
            </p>
          </div>

          {/* Share button for shared ponds - always visible on mobile */}
          {hasInviteCode && node.isOwner && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 sm:w-64" align="end" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-3">
                  <div className="text-center">
                    <p className="text-sm font-medium mb-1">Código de convite</p>
                    <div className="flex items-center gap-2 justify-center">
                      <code className="bg-muted px-2 sm:px-3 py-1 rounded-lg font-mono text-base sm:text-lg font-bold">
                        {node.inviteCode}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleCopyCode(node.inviteCode!)}
                      >
                        {copiedCode === node.inviteCode ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleShareWhatsApp(node.name, node.inviteCode!)}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleShareNative(node.name, node.inviteCode!)}
                    >
                      Compartilhar
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Navigation hints - hidden on mobile */}
          {hasChildren && (
            <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
              <ChevronDown className="h-3 w-3" />
              <span>Mergulhar</span>
            </div>
          )}
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="relative">
            {node.children.map(renderPondNode)}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-4xl h-[85vh] sm:h-[90vh] p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-pond flex items-center justify-center flex-shrink-0">
                <span className="text-lg sm:text-xl">🌊</span>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-display font-bold truncate">Mapa das Lagoas</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Navegue entre suas lagoas
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="flex-shrink-0" onClick={() => onOpenChange(false)}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Search and actions */}
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar lagoas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onExplorePublic} className="flex-1 sm:flex-none">
                <Globe className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Explorar</span>
              </Button>
              <Button variant="coral" onClick={onCreatePond} className="flex-1 sm:flex-none">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Nova Lagoa</span>
              </Button>
            </div>
          </div>

          {/* Join with invite code */}
          <div className="flex gap-2 p-2 sm:p-3 bg-muted/50 rounded-xl">
            <Input
              placeholder="Código de convite..."
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              className="font-mono uppercase text-sm"
              maxLength={8}
            />
            <Button 
              variant="secondary" 
              onClick={handleJoinPond}
              disabled={!inviteCode.trim() || joiningPond}
              className="flex-shrink-0"
            >
              {joiningPond ? "..." : "Entrar"}
            </Button>
          </div>
        </div>

        {/* Pond map */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {/* Visual background */}
          <div className="relative min-h-full">
            {/* Water background effect */}
            <div className="absolute inset-0 bg-gradient-to-b from-pond/5 via-pond/10 to-pond/5 rounded-3xl -z-10" />
            
            {filteredPonds.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 sm:py-20 text-center">
                <div className="text-5xl sm:text-6xl mb-4">🌿</div>
                <h3 className="text-base sm:text-lg font-display font-bold mb-2">
                  {searchTerm ? "Nenhuma lagoa encontrada" : "Nenhuma lagoa ainda"}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {searchTerm
                    ? "Tente outro termo de busca"
                    : "Crie sua primeira lagoa compartilhada"}
                </p>
                {!searchTerm && (
                  <Button variant="coral" onClick={onCreatePond}>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Lagoa
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredPonds.map(renderPondNode)}
              </div>
            )}
          </div>
        </div>

        {/* Footer legend - hidden on mobile */}
        <div className="hidden sm:block sticky bottom-0 bg-background/95 backdrop-blur border-t p-3">
          <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <ChevronUp className="h-3 w-3" />
              Subir para lagoa pai
            </div>
            <div className="flex items-center gap-1">
              <ChevronDown className="h-3 w-3" />
              Mergulhar em sub-lagoa
            </div>
            <div className="flex items-center gap-1">
              <Share2 className="h-3 w-3" />
              Compartilhar código
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
